import { AUTHORING_CHAT_TOOL_FILTER, ChatPanel } from "@qw/chat";
import { MCPPlatform } from "@qw/qw-common";
import { Spinner } from "@qw/ui";
import { useMemo, useRef } from "react";

import { useChatResourceLinkHandlers } from "../../chat/use-chat-resource-link-handlers";

const AUTHORING_CHAT_MODE = "authoring";
const PRODUCT_AUTHORING_ENTITY_TYPE = "product";

interface ProductEditorChatPanelProps {
  currentWorkflowMarkdown: string;
  isWorkflowMarkdownReady?: boolean;
  onInsert: (markdown: string) => void;
  productId: string;
}

export const ProductEditorChatPanel = ({
  currentWorkflowMarkdown,
  isWorkflowMarkdownReady = true,
  onInsert,
  productId,
}: ProductEditorChatPanelProps) => {
  const resourceLinkHandlers = useChatResourceLinkHandlers();
  const defaultPromptInputRef = useRef<{
    input: {
      draftContent: string;
      entityId: string;
      entityType: string;
      mode: string;
    };
    productId: string;
  } | null>(null);
  const authoringMetadata = useMemo(
    () => [
      { key: "mode", value: AUTHORING_CHAT_MODE },
      { key: "entityType", value: PRODUCT_AUTHORING_ENTITY_TYPE },
      { key: "entityId", value: productId },
    ],
    [productId]
  );
  const chatSessionsFilter = useMemo(() => ({ metadata: authoringMetadata }), [authoringMetadata]);
  const promptInput = useMemo(
    () => ({
      mode: AUTHORING_CHAT_MODE,
      entityType: PRODUCT_AUTHORING_ENTITY_TYPE,
      entityId: productId,
      draftContent: currentWorkflowMarkdown,
    }),
    [currentWorkflowMarkdown, productId]
  );

  if (!isWorkflowMarkdownReady) {
    return (
      <div className="bg-card w-full min-w-0 overflow-x-hidden px-4 py-5">
        <Spinner />
      </div>
    );
  }

  if (defaultPromptInputRef.current?.productId !== productId) {
    defaultPromptInputRef.current = { productId, input: promptInput };
  }
  const defaultPromptInput = defaultPromptInputRef.current.input;

  return (
    <ChatPanel
      apiUrl={import.meta.env.VITE_REACT_APP_API_PATH}
      chatSessionsFilter={chatSessionsFilter}
      className="bg-card w-full"
      defaultPromptInput={defaultPromptInput}
      platform={MCPPlatform.INBOX}
      promptInput={promptInput}
      resourceLinkHandlers={resourceLinkHandlers}
      suggestionInsert={{ onInsert }}
      toolFilter={AUTHORING_CHAT_TOOL_FILTER}
      toolFilterStorageKey={`authoring-product-${productId}`}
      supportsMultipleSessions
    />
  );
};
